import React from 'react';
import { motion } from 'framer-motion';
import { CurrencyDollarIcon, UserGroupIcon, PaintBrushIcon, MegaphoneIcon } from '@heroicons/react/24/outline';

const InvSlide16Ask: React.FC = () => {
  const needs = [
    {
      icon: UserGroupIcon,
      title: '2 Developers',
      desc: 'Full-stack engineers to accelerate content generation and platform features',
      color: '#0ea5e9',
    },
    {
      icon: PaintBrushIcon,
      title: '1 UX Designer',
      desc: 'Polish the student experience and create age-appropriate interfaces',
      color: '#10b981',
    },
    {
      icon: MegaphoneIcon,
      title: '2 Sales People',
      desc: 'Drive school partnerships and manage the sales pipeline',
      color: '#f59e0b',
    },
  ];

  const roadmap = [
    { phase: 'Q2 2026', goal: 'Year 8 Maths pilot with Cranbrook and Shore', status: 'active' },
    { phase: 'Q2 2026', goal: 'Catholic school expansion', status: 'upcoming' },
    { phase: 'Q3 2026', goal: 'Public school pilot (with SSO integration)', status: 'upcoming' },
    { phase: 'Q4 2026', goal: 'Interstate expansion (VIC, QLD)', status: 'upcoming' },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        The Ask
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-10 text-center"
      >
        Funding to accelerate development and go-to-market
      </motion.p>

      <div className="flex gap-10 w-full max-w-6xl">
        {/* Left: What We Need */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <h3 className="text-xl font-semibold text-white mb-5 flex items-center gap-2">
            <CurrencyDollarIcon className="w-6 h-6 text-[#10b981]" />
            What We Need
          </h3>

          <div className="space-y-4 mb-6">
            {needs.map((need) => (
              <div
                key={need.title}
                className="flex items-center gap-4 p-5 rounded-xl"
                style={{
                  background: `${need.color}10`,
                  border: `1px solid ${need.color}30`,
                }}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${need.color}20`,
                    border: `2px solid ${need.color}50`,
                  }}
                >
                  <need.icon className="w-7 h-7" style={{ color: need.color }} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">{need.title}</h4>
                  <p className="text-sm text-[#94a3b8]">{need.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            className="p-5 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <p className="text-[#94a3b8] text-sm">
              <span className="text-white font-medium">Additional:</span> Content generation, marketing,
              and school integration work (Azure AD, Google Workspaces, SAML 2.0, Active Directory)
            </p>
          </div>
        </motion.div>

        {/* Right: Roadmap */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="flex-1"
        >
          <h3 className="text-xl font-semibold text-white mb-5">Roadmap</h3>

          <div className="space-y-4">
            {roadmap.map((item) => (
              <div
                key={item.phase}
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{
                  background: item.status === 'active'
                    ? 'rgba(16, 185, 129, 0.15)'
                    : 'rgba(30, 41, 59, 0.5)',
                  border: item.status === 'active'
                    ? '2px solid rgba(16, 185, 129, 0.4)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  className="w-20 px-3 py-2 rounded-lg text-center font-semibold"
                  style={{
                    background: item.status === 'active' ? '#10b981' : 'rgba(255,255,255,0.1)',
                    color: item.status === 'active' ? 'white' : '#64748b',
                  }}
                >
                  {item.phase}
                </div>
                <div className="flex-1">
                  <p className={item.status === 'active' ? 'text-white font-medium' : 'text-[#94a3b8]'}>
                    {item.goal}
                  </p>
                </div>
                {item.status === 'active' && (
                  <div className="px-2 py-1 rounded text-xs font-bold bg-[#10b981] text-white">
                    NOW
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            className="mt-6 p-5 rounded-xl text-center"
            style={{
              background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
              border: '2px solid rgba(14, 165, 233, 0.4)',
            }}
          >
            <p className="text-white font-medium">
              Microsoft partnership potential for public school rollout
            </p>
            <p className="text-sm text-[#94a3b8] mt-1">
              Azure AD integration opens 6,700 public schools
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvSlide16Ask;
